import { useMsal } from "@azure/msal-react";
import {
    ChoiceGroup,
    DefaultButton,
    Dropdown,
    FontWeights, IDropdownOption,
    IPanelProps,
    IRenderFunction,
    Label, MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Separator,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TextField
} from "@fluentui/react";
import { RootState } from "app/store";
import SimpleCard, { ECardIcon } from "components/controls/SimpleCard";
import { Tier1To4, createBenefit, createDefaultCategoryFields, emitCustomEvent, fadeIn, panelButtonsStyles, sectionStackTokens } from "global/Application";
import { useApplication } from "global/Application/Context";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import { IPlanBenefitRelation, addBenefitReference, addUpdateBenefit, importPlanBenefits } from "global/Reducer/dataSlice";
import { JsonTransformer } from "global/Transform/JsonTransformer";
import { updateArrayReplace } from "global/Utility/TSFunctions";
import * as IXrmTypes from "model/CRM";
import { IPanelEvent, IPickItem, IValueChoiceGroupOption } from "model/Extension";
import * as FAM from "model/FlexibleAttributeModel";
import { useEffect, useState } from "react";
import { ConnectedProps, connect, useDispatch } from "react-redux";
import TierWizard from "views/TierWizard";
import { AppDataSource } from "../../global/DataSources/AppDataSource";
import BenefitCard, { EBenefitCardFunction } from "./BenefitCard";
import SnapshotLookup from "./Controls/SnapshotLookup";

interface IConfirm {
    benefitNew: FAM.IBenefit | null;
}

interface IProps extends TComponentConnectedProps {
    //TODO: unmount component with delay for animation
    isOpen: boolean;

    // cancellation of transaction/partial actions
    onCancel?: () => void;

    // dialog closure request from parent component
    onDismiss: () => void

    // use this prior to completion of primary action for verification/or to perform action on parent
    onConfirm?: (e?: IPanelEvent<IConfirm>) => void;

    // start condition
    defaultPayload?: IConfirm;

    currentPlan?: FAM.IPlan;
}

const enum EView {
    entry,
    selectBenefit,
    add,
    addProperties,
    copy,
    import,
    importProduct,
};


const BenefitWizard = (props: IProps) => {

    const dispatch = useDispatch();
    const { instance } = useMsal();
    const application = useApplication();

    const [loading, setLoading] = useState<Boolean>(false);

    // current view state/stages
    const [view, setView] = useState<EView | null>(EView.entry);
    const [validationMessage, setValidationMessage] = useState<string | null>(null);

    // selected category
    const [optionEntry, setOptionEntry] = useState<IValueChoiceGroupOption<EView> | null>(null);
    const [benefitNew, setBenefitNew] = useState<FAM.IBenefit | null>(null);

    const [importSnapshot, setImportSnapshot] = useState<IXrmTypes.bcbs_groupconfigurationsnapshot | null>();
    const [importBenefits, setImportBenefits] = useState<IPickItem<FAM.IBenefit>[] | null>(null);

    // Pick
    const currentPlan = props.currentPlan ?? null;
    const currentPlanBenefits = props.state?.data.json?.benefits.filter(q => props.currentPlan?.benefitRefs.some(w => w.id === q.id)) ?? null;
    const currentPlanCategories = props.state?.data.config?.categories?.filter(q => currentPlanBenefits.some(w => w?.bcbs_config_categoryid === q.bcbs_config_categoryid)) ?? null;

    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);


    //TODO: remove dependency
    useEffect(() => {
        // any props that we need to build state on component mount?
        // reset on start
        setBenefitNew(props.defaultPayload?.benefitNew ?? null);
        setView(EView.entry);
    }, [props.isOpen]);


    useEffect(() => {

        if (view === EView.copy)
        // reload collection if not populated already?
        {
            (async () => {
                try {
                    setLoading(true);

                    const transformer = JsonTransformer.getTransformerForCopyInternal(
                        props.state.data.config,
                        dataSource,
                        props.state.data.snapshot,
                        { applyToPlans: true, applyToBenefits: true }
                    );

                    const jClone = structuredClone(props.state.data.json)
                    const sClone = structuredClone(props.state.data.snapshot)

                    await transformer.transformAsync(jClone, sClone);

                    const data = jClone;

                    const benefits: IPickItem<FAM.IBenefit>[] = [];

                    data.benefits.forEach(eachBenefit => {

                        // transformer above is already taking care of this
                        // lets skip deactivated, they shouldn't be able to pick
                        if (eachBenefit.deactivated)
                            return;

                        benefits.push({
                            item: eachBenefit,
                            selected: false
                        });
                    });

                    setImportBenefits(benefits);
                } catch (error) {

                    // lets create a new one is payload is not providing one
                    emitCustomEvent({
                        eventType: 'globalException',
                        details: error
                    });

                } finally {
                    setLoading(false);
                }

            })();
        }
        else if (importSnapshot) {
            (async () => {

                setLoading(true);

                try {
                    const result = await dataSource.downloadGroupConfigurationSnapshot(importSnapshot);

                    if (!result)
                        throw new Error('Snapshot not found!');

                    const transformer = JsonTransformer.getTransformerForCopy(
                        props.state.data.config,
                        dataSource,
                        props.state.data.snapshot,
                        { applyToPlans: true, applyToBenefits: true }
                    );

                    await transformer.transformAsync(result.json, importSnapshot);

                    const data = result.json;

                    const benefits: IPickItem<FAM.IBenefit>[] = [];

                    data.benefits.forEach(eachBenefit => {

                        // transformer above is already taking care of this
                        // lets skip deactivated, they shouldn't be able to pick
                        if (eachBenefit.deactivated)
                            return;

                        benefits.push({
                            item: eachBenefit,
                            selected: false
                        });
                    });

                    setImportBenefits(benefits);

                    console.log('importing snapshot loaded.', importSnapshot.bcbs_filename || importSnapshot.bcbs_snapshot_name, data);

                } catch (error) {

                    emitCustomEvent({
                        eventType: 'globalException',
                        details: error
                    });

                } finally {
                    setLoading(false);
                }
            })();
        }

    }, [importSnapshot, view]);

    const optionsEntry: IValueChoiceGroupOption<EView>[] = [
        {
            key: 'add',
            text: 'Add - New',
            data: EView.add
        },
        {
            key: 'pick',
            text: 'Pick - From Current Configuration',
            data: EView.selectBenefit,
            disabled: !props.currentPlan || !application.ChangeTypePermissions.canLinkPlanBenefit
        },
        {
            key: 'copy',
            text: 'Copy/Clone - From Current Configuration',
            data: EView.copy
        },
        {
            key: 'import',
            text: 'Copy/Clone - From External Configuration/BlueRelationship',
            data: EView.import,
            disabled: false
        },
        {
            key: 'importProduct',
            text: 'Select As Product',
            data: EView.importProduct,
            disabled: false
        },
    ];

    function onRenderEntry(): JSX.Element {
        return <div>
            <ChoiceGroup
                options={optionsEntry}
                label="Choose Source"
                required={true}
                selectedKey={optionEntry?.key}
                onChange={(ev, option?: IValueChoiceGroupOption<EView>) => {
                    setOptionEntry(option ?? null);
                }}
            />
        </div >;
    }

    function onRenderEntryFooter(): JSX.Element | null {
        return <div className={fadeIn}>
            <PrimaryButton text='Continue'
                styles={panelButtonsStyles}
                disabled={optionEntry === null}
                onClick={() => {
                    setView(optionEntry?.data ?? null);
                    setImportBenefits(null);
                }}
            />
            <DefaultButton text='Cancel'
                onClick={() => { props.onCancel?.(); }}
                styles={panelButtonsStyles}
            />
        </div>;
    }

    function onRenderSelectBenefit(): JSX.Element {
        if (!importBenefits) {
            const _benefitCollection = props.state.data.json.benefits.map(t => {
                return {
                    item: t,
                    selected: false
                } as (IPickItem<FAM.IBenefit>)
            })
            setImportBenefits(_benefitCollection);

            return (
                <div>
                    Not Available
                </div>
            );
        }

        return (
            <div className={fadeIn}>
                <Text block variant="xLarge" styles={{ root: { marginBottom: 12, fontWeight: FontWeights.semilight } }}>
                    Choose benefit
                </Text>
                <Stack
                    horizontal
                    horizontalAlign="start"
                    tokens={sectionStackTokens}
                    wrap
                    verticalFill
                >
                    {importBenefits.map(eachBenefit => {
                        if (eachBenefit.item.deactivated)
                            return <></>

                        return (
                            <BenefitCard
                                state={props.state}
                                benefit={eachBenefit.item}
                                benefitCollection={importBenefits}
                                currentPlan={currentPlan}
                                currentPlanBenefits={currentPlanBenefits}
                                currentPlanCategories={currentPlanBenefits}
                                mode={EBenefitCardFunction.addToPlan}
                                icon={ECardIcon.link}
                                onClick={() => {
                                    // benefit was already picked? remove it from the list
                                    const newState = updateArrayReplace(importBenefits, eachBenefit, { ...eachBenefit, selected: !eachBenefit.selected });
                                    setImportBenefits(newState);
                                }}
                            />
                        );
                    })}
                </Stack>
                <Separator />
            </div>
        );
    }

    function onRenderSelectBenefitFooter(): JSX.Element | null {
        return <div className={fadeIn}>
            <PrimaryButton text="Continue"
                styles={panelButtonsStyles}
                disabled={!importBenefits || importBenefits?.length === 0}
                onClick={() => {
                    // any benefit picked?
                    if (importBenefits && importBenefits.length > 0 && props.currentPlan) {
                        const plan = props.currentPlan;

                        const t = importBenefits
                            .filter(q => q.selected)
                            .map(eB => {
                                const benefitRef: FAM.IBenefitRef = {
                                    id: eB.item.id,
                                    name: eB.item.name
                                };

                                return { plan: plan, benefitRef: benefitRef }
                            });

                        dispatch(addBenefitReference(t));
                        setImportBenefits([]);
                        props.onDismiss?.();
                    }
                }}
            />
            <DefaultButton text='Cancel'
                onClick={() => setView(EView.entry)}
                styles={panelButtonsStyles}
            />
        </div>;
    }

    function onRenderAdd(): JSX.Element {
        return (
            <div className={fadeIn}>
                <Text block variant="xLarge" styles={{ root: { marginBottom: 12, fontWeight: FontWeights.semilight } }}>
                    Choose benefit
                </Text>

                <Stack
                    horizontal
                    horizontalAlign="start"
                    tokens={sectionStackTokens}
                    wrap
                    verticalFill
                >
                    {props.state.data.config.categories
                        .filter(category => category._bcbs_config_segmentid_value === '69d33403-a832-ec11-b6e5-0022480ab88f')
                        //TODO: filtering to benefit segment
                        .map(category => {

                            const sameCategory = currentPlanCategories.some(q => q.bcbs_config_categoryid === category.bcbs_config_categoryid)

                            return (
                                <SimpleCard
                                    key={category.bcbs_config_categoryid}
                                    description={category.bcbs_name ?? ""}
                                    selected={category.bcbs_config_categoryid === benefitNew?.bcbs_config_categoryid}
                                    disabled={sameCategory}
                                    icons={ECardIcon.add}
                                    onClick={sameCategory
                                        ? undefined
                                        : () => {
                                            if (benefitNew === null || category.bcbs_config_categoryid !== benefitNew?.bcbs_config_categoryid) {

                                                const clone: FAM.IBenefit = createBenefit(props.state.data.config,
                                                    {
                                                        category: category,
                                                        // if not setup as dual funded, then current plan/benefit needs to default to global version of funding type
                                                        fundingType: props.state.data.json.fundingType!,
                                                        grandFathered: category.bcbs_isgrandfatherenabled ? props.state.data.json.grandFathered! : null,
                                                    }
                                                )
                                                setBenefitNew(clone);
                                            }
                                            else {
                                                setBenefitNew(null);
                                            }
                                        }}
                                />
                            );
                        })}

                </Stack>
                <Separator />

            </div>
        );
    }

    function onRenderAddFooter(): JSX.Element | null {
        return <div className={fadeIn}>
            <PrimaryButton text='Continue'
                styles={panelButtonsStyles}
                disabled={optionEntry === null || benefitNew === null}
                onClick={() => { setView(EView.addProperties); }}
            />
            <DefaultButton text='Cancel'
                onClick={() => setView(EView.entry)}
                styles={panelButtonsStyles}
            />
        </div>;
    }

    function onRenderAddProperties(): JSX.Element {

        if (!benefitNew)
            return <div>Benefit not available.</div>

        const isTiered = benefitNew.bcbs_istierenabled ?? false
        const isGrandFathered = benefitNew.bcbs_isgrandfatherenabled ?? false;
        const isFundingTypeEnabled = benefitNew.bcbs_isfundingtypeenabled ?? false;
        const isDualFunded = props.state.data.json.dualFunded?.value ?? false;

        return (
            <div className={fadeIn}>
                <Label>Benefit Properties</Label>
                <TextField
                    label="Benefit Name"
                    value={benefitNew?.name ?? ""}
                    disabled={benefitNew === null}
                    onChange={(event, value) => {
                        const t = { ...benefitNew!, name: value ?? '' };
                        setBenefitNew(t);
                    }}
                />

                {/* <Separator />
                <Dropdown
                    label={"Vendor"}
                    options={[{ key: 'BCBSRX', text: 'BCBSRX' }, { key: 'Prime', text: 'Prime' }]}
                    onChange={(event, option?: IDropdownOption<FAM.IOptionSetValue>) => { }}
                /> */}

                <Separator />
                <TierWizard
                    disabled={!isTiered}
                    variant={'new'}
                    benefit={benefitNew}
                    defaultValue={{
                        tiers: benefitNew.tiers,
                        tierSet: props.state.data.config.tiersets.find(t => t.bcbs_config_tiersetid === benefitNew.tierSetRef?.id)!
                    }}
                    onChange={(value) => {
                        if (value.tiers && value.tierSet) {
                            const t: FAM.IBenefit = {
                                ...benefitNew!,
                                tiers: value.tiers ?? [],
                                tierSetRef: {
                                    id: value.tierSet.bcbs_config_tiersetid!,
                                    name: value.tierSet.bcbs_name!
                                }
                            };
                            setBenefitNew(t);
                        }
                    }}
                />
                {!isTiered &&
                    <Text styles={{ root: { color: 'gray' } }} variant={"xSmall"}>Tier support has not been enabled for this category.</Text>
                }

                <Separator />
                <Dropdown
                    label={"Funding Type"}
                    options={props.state.data.config.cache.options_bcbs_fundingtypes ?? []}
                    disabled={!isDualFunded || !isFundingTypeEnabled}
                    selectedKey={benefitNew?.fundingType?.value}
                    onChange={(event, option?: IDropdownOption<FAM.IOptionSetValue>) => {
                        const t: FAM.IBenefit = { ...benefitNew, fundingType: option?.data ?? null }
                        setBenefitNew(t);
                    }}
                />
                {!isFundingTypeEnabled &&
                    <div>
                        <Text styles={{ root: { color: 'gray' } }} variant={"xSmall"}>Funding Type support has not been enabled for this category.</Text>
                    </div>
                }
                {!isDualFunded &&
                    <div>
                        <Text styles={{ root: { color: 'gray' } }} variant={"xSmall"}>Group configuration has not been setup for dual funding.</Text>
                    </div>
                }
                <Separator />
                <Dropdown
                    label={"Grandfathered"}
                    options={props.state.data.config.cache.options_bcbs_grandfatheredcode ?? []}
                    disabled={!isGrandFathered}
                    selectedKey={benefitNew?.grandFathered?.value}
                    onChange={(_, option?: IDropdownOption<FAM.IOptionSetValue>) => {
                        const t: FAM.IBenefit = { ...benefitNew, grandFathered: option?.data ?? null }
                        setBenefitNew(t);
                    }}
                />
                {!isGrandFathered &&
                    <Text styles={{ root: { color: 'gray' } }} variant={"xSmall"}>Grandfathered support has not been enabled for this category.</Text>
                }
            </div>
        );
    }

    function onRenderAddPropertiesFooter(): JSX.Element | null {
        if (!benefitNew)
            return <div>Benefit not available.</div>

        const disable = optionEntry === null ||
            (benefitNew.bcbs_istierenabled && benefitNew.tierSetRef === null) ||
            (benefitNew.bcbs_istierenabled && benefitNew.tiers.length > 0 && benefitNew.tiers.some(q => Tier1To4.includes(q.name) || q.name === '' || q.name === null));

        return <div className={fadeIn}>
            <PrimaryButton text='Continue'
                styles={panelButtonsStyles}
                disabled={disable}
                onClick={() => {
                    let preventDefault = false;

                    const confirm: IPanelEvent<IConfirm> = {
                        preventDefault: ({ validationMessage }) => {
                            preventDefault = true;
                        },
                        value: {
                            benefitNew: benefitNew,
                        }
                    };

                    props.onConfirm?.(confirm);


                    if (preventDefault)
                        return;

                    const fields = createDefaultCategoryFields(props.state.data.config, benefitNew, benefitNew.name, benefitNew.tiers);

                    const benefit: FAM.IBenefit = {
                        ...benefitNew!,
                        fields: fields,
                        attributeSets: [],
                        nonStandards: [],
                        status: props.state.data.config.cache.options_benefitStatusReasons.find(q => q.data?.value === FAM.EBenefitStatus["In-Progress"])?.data!,
                    }


                    dispatch(addUpdateBenefit([benefit]));
                    setBenefitNew(null);

                    if (props.currentPlan) {
                        const benefitRef: FAM.IBenefitRef = {
                            id: benefit.id,
                            name: benefit.name,
                            bcbs_config_categoryid: benefit.bcbs_config_categoryid,
                        };
                        dispatch(addBenefitReference([{ plan: props.currentPlan, benefitRef: benefitRef }]));
                        setImportBenefits([]);
                    }

                    setView(EView.entry);

                    props.onDismiss?.();
                }}
            />
            <DefaultButton text='Cancel'
                onClick={() => setView(EView.add)}
                styles={panelButtonsStyles}
            />
        </div>;
    }

    function onRenderCopy(): JSX.Element {


        // are we just displaying or limited to
        const mode: EBenefitCardFunction = props.currentPlan
            ? EBenefitCardFunction.addToPlan
            : EBenefitCardFunction.addToGroupConfiguration;

        return (
            <div className={fadeIn}>
                <Text block variant="xLarge" styles={{ root: { marginBottom: 12, fontWeight: FontWeights.semilight } }}>
                    Choose benefit
                </Text>

                <Stack
                    horizontal
                    horizontalAlign="start"
                    tokens={sectionStackTokens}
                    wrap
                    verticalFill
                >
                    {
                        loading &&
                        <Spinner label="Please wait..." ariaLive="assertive" labelPosition="right" size={SpinnerSize.large} styles={{ root: { padding: '25px' } }} />
                    }

                    {importBenefits?.map(eachBenefit => {
                        return (
                            <BenefitCard
                                key={eachBenefit.item.id}
                                state={props.state}
                                benefit={eachBenefit.item}
                                benefitCollection={importBenefits}
                                currentPlan={currentPlan}
                                currentPlanBenefits={currentPlanBenefits}
                                currentPlanCategories={currentPlanCategories}
                                mode={mode}
                                icon={ECardIcon.copy}
                                onClick={() => {
                                    // benefit was already picked? remove it from the list
                                    const newState = updateArrayReplace(importBenefits, eachBenefit, { ...eachBenefit, selected: !eachBenefit.selected });
                                    setImportBenefits(newState);
                                }}
                            />
                        );
                    })}
                </Stack>
                <Separator />
            </div>
        );
    }

    function onRenderCopyFooter(): JSX.Element | null {
        return <div className={fadeIn}>
            <PrimaryButton text='Continue'
                styles={panelButtonsStyles}
                disabled={!importBenefits?.find(t => t.selected)}
                onClick={() => {
                    const bSelects = importBenefits?.filter(q => q.selected).map(q => q.item) ?? [];
                    const bClones = bSelects

                    bClones.forEach(benefit => {
                        benefit.name = benefit.name + ' - Copied';
                        benefit.status = {
                            "type": "optionset",
                            "name": FAM.EBenefitStatus[FAM.EBenefitStatus["In-Progress"]],
                            "value": FAM.EBenefitStatus["In-Progress"]
                        }
                    });

                    dispatch(addUpdateBenefit(bClones));

                    // any benefit picked?
                    if (props.currentPlan) {
                        const plan = props.currentPlan;

                        const benefitRefs: IPlanBenefitRelation[] = bClones.map(benefit => {
                            const benefitRef: FAM.IBenefitRef = {
                                id: benefit.id,
                                name: benefit.name
                            };

                            return { plan: plan, benefitRef: benefitRef }
                        });

                        dispatch(addBenefitReference(benefitRefs));
                    }

                    setImportBenefits(null);
                    setImportSnapshot(null);

                    props.onDismiss?.();
                }}
            />
            <DefaultButton text='Cancel'
                onClick={() => setView(EView.entry)}
                styles={panelButtonsStyles}
            />
        </div>;
    }

    function onRenderImport(): JSX.Element {

        // are we just displaying or limited to
        const mode: EBenefitCardFunction = props.currentPlan
            ? EBenefitCardFunction.addToPlan
            : EBenefitCardFunction.addToGroupConfiguration;

        let useAccount: FAM.IEntityReferenceValue = {
            type: 'entity',
            entityType: 'account',
            id: 'cb78766c-4ed5-ec11-a7b5-000d3a3ab609',
            name: 'Template Only',
        }

        return <Stack tokens={{ childrenGap: '15' }}>

            <Text block variant="xLarge" styles={{ root: { marginBottom: 12, fontWeight: FontWeights.semilight } }}>
                Copy benefits
            </Text>

            <SnapshotLookup
                onPick={async (value) => {
                    const snapshot = await dataSource.getGroupConfigurationSnapshot(value?.id!);
                    setImportSnapshot(snapshot);
                }}
                defaultValueAccount={view === EView.importProduct ? useAccount : undefined}
                defaultValueViewKey={view === EView.importProduct ? "inProgressAndGoldStandardView" : undefined}
                valueAccount={view === EView.importProduct ? useAccount : undefined}
                searchOnMount={true}
            />

            {
                loading &&
                <Spinner label="Please wait..." ariaLive="assertive" labelPosition="right" size={SpinnerSize.large} styles={{ root: { padding: '25px' } }} />
            }
            <Stack
                horizontal
                horizontalAlign="start"
                tokens={sectionStackTokens}
                wrap
                verticalFill
            >
                {importBenefits?.map(eachBenefit => {
                    return (
                        <BenefitCard
                            state={props.state}
                            benefit={eachBenefit.item}
                            benefitCollection={importBenefits}
                            currentPlan={currentPlan}
                            currentPlanBenefits={currentPlanBenefits}
                            currentPlanCategories={currentPlanCategories}
                            deactivated={eachBenefit.item.deactivated}
                            mode={mode}
                            icon={ECardIcon.copy}
                            onClick={() => {
                                // benefit was already picked? remove it from the list
                                const newState = updateArrayReplace(importBenefits, eachBenefit, { ...eachBenefit, selected: !eachBenefit.selected });
                                setImportBenefits(newState);
                            }}
                        />
                    );
                })}
            </Stack>
        </Stack>;
    }

    function onRenderImportFooter(): JSX.Element | null {
        return <div className={fadeIn}>
            <PrimaryButton text='Continue'
                styles={panelButtonsStyles}
                disabled={!importBenefits || !importBenefits?.some(q => q.selected)}
                onClick={() => {

                    const benefits = importBenefits?.filter(q => q.selected).map(q => q.item)!;

                    dispatch(importPlanBenefits({
                        plan: props.currentPlan,
                        benefits: benefits,
                        config: props.state.data.config
                    }));

                    props.onDismiss?.();
                }}
            />
            <DefaultButton text='Cancel'
                onClick={() => setView(EView.entry)}
                styles={panelButtonsStyles}
            />
        </div>;
    }

    // const onRenderHeader: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
    //     return (
    //         <div className={contextProps?.headerClassName} style={{ width: '100%', paddingLeft: '24px', fontSize: '24px', fontWeight: 'bold' }}>
    //             {view === EView.addProperties &&
    //                 <IconButton iconProps={{ iconName: 'NavigateBack' }} onClick={() => { setView(EView.add) }} />
    //             }
    //             {contextProps?.headerText}
    //         </div>
    //     );
    // }

    const onRenderBody: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
        let onRender = onRenderUnknown;

        switch (view) {
            case EView.entry:
                onRender = onRenderEntry;
                break;

            case EView.selectBenefit:
                onRender = onRenderSelectBenefit;
                break;

            case EView.add:
                onRender = onRenderAdd;
                break;

            case EView.addProperties:
                onRender = onRenderAddProperties;
                break;

            case EView.copy:
                onRender = onRenderCopy;
                break;

            case EView.import:
                onRender = onRenderImport;
                break;

            case EView.importProduct:
                onRender = onRenderImport;
                break;
        }

        return (
            <div >
                <Separator />
                {onRender()}
            </div>
        );

    }

    const onRenderUnknown: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
        return (<div>Unknown</div>)
    }

    const onRenderFooterContent: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
        let onRender = onRenderUnknown;

        switch (view) {
            case EView.entry:
                onRender = onRenderEntryFooter;
                break;

            case EView.add:
                onRender = onRenderAddFooter;
                break;

            case EView.addProperties:
                onRender = onRenderAddPropertiesFooter;
                break;

            case EView.selectBenefit:
                onRender = onRenderSelectBenefitFooter;
                break;

            case EView.copy:
                onRender = onRenderCopyFooter;
                break;

            case EView.import:
                onRender = onRenderImportFooter;
                break;

            case EView.importProduct:
                onRender = onRenderImportFooter;
                break;
        }

        return onRender();

    }

    return (
        <Panel
            isOpen={props.isOpen}
            onDismiss={props.onCancel}
            type={PanelType.medium}
            closeButtonAriaLabel="Close"
            headerText="Add | New Benefit"
            // onRenderHeader={onRenderHeader}
            onRenderFooterContent={onRenderFooterContent}
            isFooterAtBottom={true}
        >
            {onRenderBody()}
        </Panel>
    )
}


function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(BenefitWizard);
